/**
 * bQuest Preferred Providers curated section (Feature 083)
 * Self-contained: fetches the global curated list and the care seeker's saved
 * professionals (React Query dedupes both), then renders below the saved
 * professionals on the My Team → Professionals tab. Hidden when empty.
 */

import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { getFileUrl } from '@/shared/hooks/useFileUpload';
import { CuratedSection } from './CuratedSection';
import { PreferredBadge } from './PreferredBadge';
import {
  useCuratedTeam,
  useAllFavorites,
  useAddFavoriteProfessional,
  useRemoveFavoriteProfessional,
} from '../hooks/useFavorites';
import type { ProfessionalUser } from '../types';

const SUBTITLE =
  'Trusted partners we recommend to every family — vetted and onboarded by the bQuest care team.';

export function CuratedProvidersSection() {
  const navigate = useNavigate();
  const { data: curated, isLoading, isError } = useCuratedTeam();
  const { data: favorites } = useAllFavorites();
  const addFavorite = useAddFavoriteProfessional();
  const removeFavorite = useRemoveFavoriteProfessional();

  if (isLoading) {
    return (
      <div className="mt-8 space-y-3" data-testid="curated-providers-skeleton">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="mt-8 p-6 text-center">
        <p className="text-red-600">We couldn&apos;t load bQuest Preferred Providers right now.</p>
      </Card>
    );
  }

  const providers = curated?.preferredProviders ?? [];
  if (providers.length === 0) {
    return null;
  }

  const savedIds = new Set((favorites?.professionals ?? []).map((f) => f.professional.id));

  const toggleSave = (professionalUserId: string, isSaved: boolean) => {
    if (isSaved) {
      removeFavorite.mutate(professionalUserId);
    } else {
      addFavorite.mutate({ professionalUserId });
    }
  };

  return (
    <CuratedSection title="bQuest Preferred Providers" subtitle={SUBTITLE}>
      <div className="grid md:grid-cols-2 gap-6">
        {providers.map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            isSaved={savedIds.has(provider.id)}
            onToggleSave={toggleSave}
            onView={(path) => navigate(path)}
            onContact={(userId) => navigate(`/messages?recipient=${userId}`)}
          />
        ))}
      </div>
    </CuratedSection>
  );
}

type ProviderCardProps = {
  provider: ProfessionalUser;
  isSaved: boolean;
  onToggleSave: (professionalUserId: string, isSaved: boolean) => void;
  onView: (path: string) => void;
  onContact: (userId: string) => void;
};

function ProviderCard({ provider, isSaved, onToggleSave, onView, onContact }: ProviderCardProps) {
  const sp = provider.serviceProvider;
  const name = provider.name || sp?.companyName || 'Unknown Professional';
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const detailsPath = sp?.id
    ? `/providers/${sp.id}?discoverySource=FAVORITES`
    : null;
  const photoUrl = provider.profilePhoto
    ? provider.profilePhoto.startsWith('http')
      ? provider.profilePhoto
      : getFileUrl(provider.profilePhoto)
    : null;

  return (
    <Card className="p-6 bg-white">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <Avatar className="w-16 h-16 flex-shrink-0">
            {photoUrl ? (
              <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
            ) : (
              <AvatarFallback className="bg-[#026857] text-white text-lg">{initials}</AvatarFallback>
            )}
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{name}</h3>
            <p className="text-sm text-gray-600 mb-2 truncate">{sp?.companyName || 'N/A'}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{provider.role}</Badge>
              <PreferredBadge />
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="flex-shrink-0"
          aria-label={isSaved ? 'Remove from saved' : 'Save professional'}
          onClick={() => onToggleSave(provider.id, isSaved)}
        >
          <Heart
            aria-hidden="true"
            className={isSaved ? 'w-5 h-5 fill-red-500 text-red-500' : 'w-5 h-5 text-gray-400'}
          />
        </Button>
      </div>
      <div className="flex gap-2 mt-4">
        <Button
          className="flex-1 bg-[#026857] hover:bg-[#025247]"
          onClick={() => detailsPath && onView(detailsPath)}
          disabled={!detailsPath}
        >
          View Details
        </Button>
        <Button variant="outline" className="flex-1" onClick={() => onContact(provider.id)}>
          <MessageCircle className="w-4 h-4 mr-2" />
          Contact
        </Button>
      </div>
    </Card>
  );
}
